import { useQuery } from "@tanstack/react-query";
import { MessageSquareText, RefreshCw } from "lucide-react";
import type { Identifier } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  DashboardRequestError,
  dashboardRequest,
  isDashboardDataProviderEnabled,
} from "../../providers/aws/client";
import { formatDateTime } from "../sessionUtils";
import {
  FILM_CHANGE_REASON_LABELS,
  FILM_CHANGE_REQUESTS_QUERY_PREFIX,
  filmChangeRequestsPath,
  parseFilmChangeRequests,
} from "./filmChangeRequests";

/** A 4xx (no such Echo, or a dashboard-api without the route yet) will not change on a retry. */
function isClientError(error: unknown) {
  return (
    error instanceof DashboardRequestError &&
    error.status >= 400 &&
    error.status < 500
  );
}

/**
 * WRP-062: the customer's own words when they asked the team to change this
 * film on my.echoflicks.com ("Ask us to change it"), because it can't be
 * changed there. Shown only when there is a request, next to the media bin and
 * the Remakes card the operator re-cut lands on. Read-only.
 */
export function FilmChangeRequestsCard({
  sessionId,
}: {
  sessionId: Identifier;
}) {
  const enabled = isDashboardDataProviderEnabled();
  const query = useQuery({
    queryKey: [FILM_CHANGE_REQUESTS_QUERY_PREFIX, String(sessionId)],
    queryFn: async ({ signal }) =>
      parseFilmChangeRequests(
        await dashboardRequest<unknown>(filmChangeRequestsPath(sessionId), {
          signal,
        }),
      ),
    enabled,
    staleTime: 30_000,
    retry: (failureCount, error) => !isClientError(error) && failureCount < 2,
  });

  if (!enabled) return null;
  if (query.isError && !isClientError(query.error)) {
    return (
      <p
        role="alert"
        className="text-sm text-muted-foreground"
        data-testid="film-change-requests-error"
      >
        Customer change requests could not load.{" "}
        <button
          type="button"
          className="underline"
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </p>
    );
  }
  const requests = query.data ?? [];
  if (!requests.length) return null;

  return (
    <Card data-testid="film-change-requests">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <MessageSquareText className="size-4" aria-hidden />
              Customer asked us to change this film
            </CardTitle>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Sent from my.echoflicks.com, where this film can’t be changed by
              the customer. Make the change with an operator re-cut
              (docs/reprompt/runbook.md §3), then reply on WhatsApp.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh change requests"
          >
            <RefreshCw
              className={query.isFetching ? "size-4 animate-spin" : "size-4"}
              aria-hidden
            />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {requests.map((request) => (
            <li
              key={request.id}
              className="space-y-2"
              data-testid="film-change-request"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <time dateTime={request.createdAt}>
                  {formatDateTime(request.createdAt)}
                </time>
                {request.source ? <span>· {request.source}</span> : null}
                {request.reason ? (
                  <Badge variant="outline">
                    {FILM_CHANGE_REASON_LABELS[request.reason]}
                  </Badge>
                ) : null}
              </div>
              <blockquote className="whitespace-pre-wrap break-words border-l-2 pl-3 text-sm">
                {request.message}
              </blockquote>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
